import type { Screen, Option } from '../types';
import type { ValidationWorkspaceTab } from '../stores/usePcfValidationStore';
import type { PluginUpdateSnapshot } from '../api/grpc/pluginUpdateClient';
import type { ParameterAdditionResult } from '../lib/hooks/parameterAddition/models';

const PLUGIN_UPDATE_STATES = ['available', 'uptodate', 'failed'] as const;
const MAPPING_PROFILE_PROGRESS_STATES = ['repairing', 'failed'] as const;
const CONNECTOR_PARAMETER_OUTCOMES = ['failure', 'success', 'recovery-required'] as const;
/**
 * Outcomes the Connectors page may report on its own. `recovery-required` is
 * deliberately absent: it describes what the model was left holding after a real
 * undo, so it must come out of the connector target transaction rather than a
 * page-level stub that never touched the checkpoint.
 */
const STUBBED_CONNECTOR_PARAMETER_OUTCOMES = ['failure', 'success'] as const;
const PENDING_CONNECTOR_OPERATIONS = ['family-type'] as const;
/**
 * Setup-revision positions a run may be seeded into. `reopened` holds the setup
 * boundary screen; `workspace-blocked` holds a mounted workspace behind the
 * same revision lock so Playwright can exercise its user-visible feedback.
 */
const SETUP_REVISION_STATES = ['reopened', 'workspace-blocked'] as const;

export type E2ePluginUpdateState = (typeof PLUGIN_UPDATE_STATES)[number];
export type E2eMappingProfileProgressState = (typeof MAPPING_PROFILE_PROGRESS_STATES)[number];
export type E2eConnectorParameterOutcome = (typeof CONNECTOR_PARAMETER_OUTCOMES)[number];
export type E2eStubbedConnectorOutcome = (typeof STUBBED_CONNECTOR_PARAMETER_OUTCOMES)[number];
export type E2ePendingConnectorOperation = (typeof PENDING_CONNECTOR_OPERATIONS)[number];
export type E2eSetupRevisionState = (typeof SETUP_REVISION_STATES)[number];

const SCREENS: Screen[] = [
  'welcome',
  'menu',
  'boltConnection',
  'connectorBinding',
  'processProperties',
  'additionalCategories',
  'mappingConfiguration',
  'quickFillProperties',
  'pcfValidation',
];

const WORKSPACE_TABS: ValidationWorkspaceTab[] = [
  'mapping',
  'quickFill',
  'validation',
  'export',
];

const VALIDATION_SURFACES = [
  'console',
  'repairDetail',
  'conditionStudio',
  'configure',
] as const;
const VALIDATION_REPAIR_PATHS = ['direct', 'guided', 'review'] as const;

export type E2eValidationSurface = (typeof VALIDATION_SURFACES)[number];
export type E2eValidationRepairPath = (typeof VALIDATION_REPAIR_PATHS)[number];

const OPTION_LOOKUP: Record<string, Exclude<Option, null>> = {
  iso: 'Iso Drawings Generation',
  isodrawingsgeneration: 'Iso Drawings Generation',
  pressuredrop: 'Pressure Drop Calculations',
  pressuredropcalculations: 'Pressure Drop Calculations',
  stress: 'Stress Calculations',
  stresscalculations: 'Stress Calculations',
};

function normalizeToken(value: string | null): string {
  return (value || '').trim().toLowerCase().replace(/[^a-z0-9]/g, '');
}

function parseScreen(value: string | null): Screen | null {
  const raw = (value || '').trim();
  return SCREENS.includes(raw as Screen) ? (raw as Screen) : null;
}

function parseWorkflowOption(value: string | null): Option {
  const normalized = normalizeToken(value);
  return OPTION_LOOKUP[normalized] || null;
}

function parseWorkspaceTab(value: string | null): ValidationWorkspaceTab | null {
  const raw = (value || '').trim();
  return WORKSPACE_TABS.includes(raw as ValidationWorkspaceTab)
    ? (raw as ValidationWorkspaceTab)
    : null;
}

function parseValidationSurface(value: string | null): E2eValidationSurface | null {
  const raw = (value || '').trim();
  return VALIDATION_SURFACES.includes(raw as E2eValidationSurface)
    ? (raw as E2eValidationSurface)
    : null;
}

function parseValidationRepairPath(value: string | null): E2eValidationRepairPath | null {
  const raw = (value || '').trim();
  return VALIDATION_REPAIR_PATHS.includes(raw as E2eValidationRepairPath)
    ? (raw as E2eValidationRepairPath)
    : null;
}

function parsePluginUpdate(value: string | null): E2ePluginUpdateState | null {
  const raw = (value || '').trim().toLowerCase();
  return PLUGIN_UPDATE_STATES.includes(raw as E2ePluginUpdateState)
    ? (raw as E2ePluginUpdateState)
    : null;
}

function parseMappingProfileProgress(value: string | null): E2eMappingProfileProgressState | null {
  const raw = (value || '').trim().toLowerCase();
  return MAPPING_PROFILE_PROGRESS_STATES.includes(raw as E2eMappingProfileProgressState)
    ? (raw as E2eMappingProfileProgressState)
    : null;
}

function parseConnectorParameterOutcome(value: string | null): E2eConnectorParameterOutcome | null {
  const raw = (value || '').trim().toLowerCase();
  return CONNECTOR_PARAMETER_OUTCOMES.includes(raw as E2eConnectorParameterOutcome)
    ? (raw as E2eConnectorParameterOutcome)
    : null;
}

function parsePendingConnectorOperation(
  value: string | null
): E2ePendingConnectorOperation | null {
  const raw = (value || '').trim().toLowerCase();
  return PENDING_CONNECTOR_OPERATIONS.includes(raw as E2ePendingConnectorOperation)
    ? (raw as E2ePendingConnectorOperation)
    : null;
}

function parseSetupRevision(value: string | null): E2eSetupRevisionState | null {
  const raw = (value || '').trim().toLowerCase();
  return SETUP_REVISION_STATES.includes(raw as E2eSetupRevisionState)
    ? (raw as E2eSetupRevisionState)
    : null;
}

/** Narrows a parsed outcome to the ones the page itself may answer with. */
export function asStubbedConnectorOutcome(
  outcome: E2eConnectorParameterOutcome | null
): E2eStubbedConnectorOutcome | null {
  return outcome !== null &&
    STUBBED_CONNECTOR_PARAMETER_OUTCOMES.includes(outcome as E2eStubbedConnectorOutcome)
    ? (outcome as E2eStubbedConnectorOutcome)
    : null;
}

export function buildE2eConnectorParameterResult(
  outcome: E2eStubbedConnectorOutcome
): ParameterAdditionResult {
  if (outcome === 'success') {
    return {
      success: true,
      addedCount: 11,
      existingCount: 3,
      requestedCount: 14,
      undoAvailable: false,
    };
  }

  return {
    success: false,
    addedCount: 11,
    failedCount: 1,
    conflictingCount: 1,
    requestedCount: 14,
    failedParameters: [
      'PiX_GN_2_InsulationSpec: Parameter not found in the shared parameter file',
    ],
    conflictingParameters: [
      'PiX_03_Material_ENG: Existing TEXT data type conflicts with requested NUMBER data type',
    ],
    failureMessages: ['Revit could not finish the connector parameter transaction'],
    error: '2 connector parameters need attention before you can\u00A0continue.',
  };
}

/**
 * Build a deterministic plugin-update snapshot for screenshot tests. The
 * embedded gRPC server is absent in e2e, so the prompt is seeded directly via
 * AppContent's e2e bootstrap effect.
 */
const pluginUpdateInstallDefaults = {
  downloadProgressPercent: 0,
  stagedVersion: '',
  failureReason: '',
  canRetry: false,
  isBelowMinSupported: false,
  minSupportedVersion: '',
  autoDownloadEnabled: true,
} as const;

export function buildE2ePluginUpdateSnapshot(
  state: E2ePluginUpdateState
): PluginUpdateSnapshot {
  if (state === 'available') {
    return {
      state: 'UPDATE_AVAILABLE',
      ...pluginUpdateInstallDefaults,
      installedVersion: '1.2.0',
      latestVersion: '1.4.0',
      channel: 'stable',
      sha256: 'e2e0000000000000000000000000000000000000000000000000000000000000',
      sizeBytes: 18_874_368,
      releaseNotesUrl: 'https://pipeex.example/release-notes/1.4.0',
      downloadUrl: 'https://pipeex.example/download/1.4.0',
      lastCheckedAtUtc: '2026-06-07T12:00:00Z',
      errorMessage: '',
    };
  }

  if (state === 'failed') {
    return {
      state: 'CHECK_FAILED',
      ...pluginUpdateInstallDefaults,
      installedVersion: '1.2.0',
      latestVersion: '',
      channel: 'stable',
      sha256: '',
      sizeBytes: 0,
      releaseNotesUrl: '',
      downloadUrl: '',
      lastCheckedAtUtc: '',
      errorMessage: 'Could not reach the update server',
    };
  }

  return {
    state: 'UP_TO_DATE',
    ...pluginUpdateInstallDefaults,
    installedVersion: '1.4.0',
    latestVersion: '1.4.0',
    channel: 'stable',
    sha256: 'e2e0000000000000000000000000000000000000000000000000000000000000',
    sizeBytes: 18_874_368,
    releaseNotesUrl: 'https://pipeex.example/release-notes/1.4.0',
    downloadUrl: 'https://pipeex.example/download/1.4.0',
    lastCheckedAtUtc: '2026-06-07T12:00:00Z',
    errorMessage: '',
  };
}

export interface E2eBootstrapConfig {
  screen: Screen | null;
  workflowOption: Option;
  validationMock: boolean;
  validationMockDelay: boolean;
  validationMockContextChange: boolean;
  workspaceTab: ValidationWorkspaceTab | null;
  validationSurface: E2eValidationSurface | null;
  validationRepairPath: E2eValidationRepairPath | null;
  pluginUpdate: E2ePluginUpdateState | null;
  mappingProfileProgress: E2eMappingProfileProgressState | null;
  connectorParameterOutcome: E2eConnectorParameterOutcome | null;
  pendingConnectorOperation: E2ePendingConnectorOperation | null;
  setupRevision: E2eSetupRevisionState | null;
}

export function getE2eBootstrapConfig(
  search: string,
  nodeEnv: string | undefined = process.env.NODE_ENV
): E2eBootstrapConfig | null {
  const e2eBuildEnabled = process.env.REACT_APP_PIPEEX_E2E_BYPASS === '1';
  if (nodeEnv !== 'development' && !e2eBuildEnabled) {
    return null;
  }

  const params = new URLSearchParams(search);
  if (params.get('e2e') !== '1') {
    return null;
  }

  return {
    screen: parseScreen(params.get('e2eScreen')),
    workflowOption: parseWorkflowOption(params.get('e2eWorkflow')),
    validationMock: params.get('e2eMockValidation') === '1',
    validationMockDelay: params.get('e2eMockValidationDelay') === '1',
    validationMockContextChange: params.get('e2eMockValidationContextChange') === '1',
    workspaceTab: parseWorkspaceTab(params.get('e2eWorkspaceTab')),
    validationSurface: parseValidationSurface(params.get('e2eValidationSurface')),
    validationRepairPath: parseValidationRepairPath(params.get('e2eValidationRepairPath')),
    pluginUpdate: parsePluginUpdate(params.get('e2ePluginUpdate')),
    mappingProfileProgress: parseMappingProfileProgress(params.get('e2eMappingProfileProgress')),
    connectorParameterOutcome: parseConnectorParameterOutcome(
      params.get('e2eConnectorParameterOutcome')
    ),
    pendingConnectorOperation: parsePendingConnectorOperation(
      params.get('e2ePendingConnectorOperation')
    ),
    setupRevision: parseSetupRevision(params.get('e2eSetupRevision')),
  };
}

export function buildE2eNavigationHistory(
  screen: Screen,
  workflowOption: Option
): Screen[] {
  if (screen === 'mappingConfiguration') {
    const history: Screen[] = [
      'welcome',
      'menu',
      'connectorBinding',
      'quickFillProperties',
      'boltConnection',
    ];

    if (workflowOption === 'Iso Drawings Generation') {
      history.push('additionalCategories');
    }

    history.push('processProperties', 'mappingConfiguration');
    return history;
  }

  if (screen === 'pcfValidation') {
    return ['welcome', 'pcfValidation'];
  }

  if (screen === 'menu') {
    return ['welcome', 'menu'];
  }

  return ['welcome', screen];
}
